import {
  Box,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Button,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import FormHeader from "./FormHeader";
import { LANG } from "./utils/languageConfig";

/* ---------------- OPTIONS (UNCHANGED) ---------------- */
const complexionOptions = [
  "Very Fair",
  "Fair",
  "Light Neutral",
  "Wheatish",
  "Dark",
  "Brown",
  "Dark Brown",
];

const heightOptions = [
  "3 ft. 5 in. (104 cm)", "3 ft. 6 in. (106 cm)", "3 ft. 7 in. (109 cm)",
  "3 ft. 8 in. (112 cm)", "3 ft. 9 in. (114 cm)", "3 ft. 10 in. (117 cm)",
  "3 ft. 11 in. (120 cm)", "4 ft. (122 cm)", "4 ft. 1 in. (124 cm)",
  "4 ft. 2 in. (127 cm)", "4 ft. 3 in. (130 cm)", "4 ft. 4 in. (132 cm)",
  "4 ft. 5 in. (135 cm)", "4 ft. 6 in. (137 cm)", "4 ft. 7 in. (140 cm)",
  "4 ft. 8 in. (142 cm)", "4 ft. 9 in. (145 cm)", "4 ft. 10 in. (147 cm)",
  "4 ft. 11 in. (149 cm)", "5 ft. (152 cm)", "5 ft. 1 in. (154 cm)",
  "5 ft. 2 in. (157 cm)", "5 ft. 3 in. (160 cm)", "5 ft. 4 in. (163 cm)",
  "5 ft. 5 in. (165 cm)", "5 ft. 6 in. (168 cm)", "5 ft. 7 in. (170 cm)",
  "5 ft. 8 in. (173 cm)", "5 ft. 9 in. (175 cm)", "5 ft. 10 in. (178 cm)",
  "5 ft. 11 in. (180 cm)", "6 ft. (183 cm)", "6 ft. 1 in. (185 cm)",
  "6 ft. 2 in. (188 cm)", "6 ft. 3 in. (190 cm)", "6 ft. 4 in. (193 cm)",
  "6 ft. 5 in. (195 cm)", "6 ft. 6 in. (198 cm)", "6 ft. 7 in. (201 cm)",
  "6 ft. 8 in. (203 cm)", "6 ft. 9 in. (206 cm)", "6 ft. 10 in. (208 cm)",
  "6 ft. 11 in. (211 cm)", "7 ft. (213 cm)",
];

const rashiOptions = [
  "Mesh (Aries)", "Vrishabh (Taurus)", "Mithun (Gemini)", "Karka (Cancer)",
  "Simha (Leo)", "Kanya (Virgo)", "Tula (Libra)", "Vrishchik (Scorpio)",
  "Dhanu (Sagittarius)", "Makar (Capricorn)", "Kumbh (Aquarius)", "Meen (Pisces)",
];

const nakshatraOptions = [
  "Ashwini", "Bharani", "Krittika", "Rohini", "Mrigashirsha", "Ardra",
  "Punarvasu", "Pushya", "Ashlesha", "Magha", "Purva Phalguni",
  "Uttara Phalguni", "Hasta", "Chitra", "Swati", "Vishakha",
  "Anuradha", "Jyeshtha", "Mula", "Purva Ashadha", "Uttara Ashadha",
  "Shravana", "Dhanishtha", "Shatabhisha",
  "Purva Bhadrapada", "Uttara Bhadrapada", "Revati",
];

/* ✅ FIXED FUNCTION */
const getOptions = (f, language) => {
  if (f.label === "Gender")
    return LANG?.[language]?.options?.gender || ["Male", "Female", "Prefer not to say"];

  if (f.label === "Rashi")
    return LANG?.[language]?.options?.rashi || rashiOptions;

  if (f.label === "Complexion")
    return LANG?.[language]?.options?.complexion || complexionOptions;

  if (f.label === "Height") return heightOptions;

  if (f.label === "Nakshatra") return nakshatraOptions;

  return [];
};

function FieldsSection({
  groups,
  fields,
  handleLabelChange,
  handleChange,
  moveUp,
  moveDown,
  deleteField,
  addField,
  handleDownloadPdf,
  validationErrors = {},
  language,
}) {
  return (
    <>
      <FormHeader />

      <Paper sx={{ p: 1 }}>
        <Box id="fieldsSection">
          {groups.map((g) => (
            <Box key={g} mb={2}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  px: 1,
                  py: 0.5,
                  mb: 0.75,
                  borderRadius: 1,
                  background: "linear-gradient(135deg,#ffe0b2,#ffcc80)",
                }}
              >
                {LANG?.[language]?.groups?.[g] || g}
              </Typography>

                {fields
                  .filter((f) => {
                    const langConfig = LANG[language]?.fields;

                    // ❌ exclude fields
                    if (langConfig?.exclude?.includes(f.label)) return false;

                    // ✅ include only specific fields (if defined)
                    if (langConfig?.include?.length > 0) {
                      return langConfig.include.includes(f.label) || f.required;
                    }

                    // default → show all
                    return true;
                  })
                      .map((f) =>
                        f.group === g && (
                    <Paper
                      key={f.id}
                      id={`field-${f.id}`}
                      sx={{
                        p: 0.75,
                        mb: 0.5,
                        display: "flex",
                        gap: 0.75,
                        borderRadius: 1.5,
                        border: "1px solid rgba(0,0,0,0.06)",
                        backgroundColor: "#fafafa",
                      }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: { xs: "column", md: "row" },
                          gap: 0.75,
                        }}
                      >
                        {/* LABEL */}
                        <TextField
                          size="small"
                          label={
                            f.required ? (
                              <>
                                {LANG?.[language]?.labels?.[f.label] || f.label}
                                <span style={{ color: "red" }}>*</span>
                              </>
                            ) : (
                              LANG?.[language]?.labels?.[f.label] || f.label
                            )
                          }
                          value={LANG?.[language]?.labels?.[f.label] || f.label}
                          disabled={f.required}
                          onChange={(e) => handleLabelChange(f.id, e.target.value)}
                          sx={{
                            flex: { md: 1 },
                            backgroundColor: "#f3f3f3",
                            "& input": {
                              fontWeight: 600,
                              color: "#555",
                            },
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor: "#555",
                            },
                          }}
                        />

                        {/* VALUE */}
                        {f.label === "Gender" ||
                        f.label === "Rashi" ||
                        f.label === "Complexion" ||
                        f.label === "Height" ||
                        f.label === "Nakshatra" ? (
                        <Select
                          size="small"
                          fullWidth
                          value={f.value || ""}
                          onChange={(e) => handleChange(f.id, e.target.value)}
                          displayEmpty
                          renderValue={(selected) => {
                            // 👉 Show placeholder when nothing selected
                            if (!selected) {
                              return (
                                <span style={{ color: "#999" }}>
                                  {LANG?.[language]?.placeholders?.[f.label] || "Select option"}
                                </span>
                              );
                            }

                            // 👉 Show selected value (localized)
                            return LANG?.[language]?.optionsMap?.[selected] || selected;
                          }}
                          sx={{
                            flex: { md: 1 },
                            backgroundColor: "#fff",
                            borderLeft: validationErrors[f.key || f.label]
                              ? "4px solid #f57c00"
                              : "none",
                          }}
                        >
                          {/* 🔥 Placeholder option (important) */}
                          <MenuItem value="" disabled>
                            {LANG?.[language]?.placeholders?.[f.label] || "Select option"}
                          </MenuItem>

                          {/* 🔥 Real options */}
                          {getOptions(f, language).map((opt) => (
                            <MenuItem key={opt} value={opt}>
                              {LANG?.[language]?.optionsMap?.[opt] || opt}
                            </MenuItem>
                          ))}
                        </Select>
                        ) : (
                          <TextField
                            size="small"
                            fullWidth
                            type={
                              f.label === "Date Of Birth"
                                ? "date"
                                : f.label === "Time Of Birth"
                                ? "time"
                                : "text"
                            }
                            value={f.value}
                            onChange={(e) =>
                              handleChange(f.id, e.target.value)}
                            error={Boolean(validationErrors[f.key || f.label])}
                            helperText={
                              validationErrors[f.key || f.label]
                                ? "Required Field"
                                : ""
                            }
                            placeholder={
                              LANG?.[language]?.placeholders?.[f.label] ||
                              LANG?.[language]?.placeholders?.default
                            }
                            sx={{
                              flex: { md: 1 },
                              backgroundColor: "#fff",
                              "& .MuiOutlinedInput-root": {
                                fontWeight: 500,
                              },
                            }}
                          />
                        )}
                      </Box>

                      {/* CONTROLS */}
                      <Box display="flex" gap={0.25}>
                        <Box display="flex" flexDirection="column">
                          <IconButton size="small" onClick={() => moveUp(f.id)}>
                            ▲
                          </IconButton>
                          <IconButton size="small" onClick={() => moveDown(f.id)}>
                            ▼
                          </IconButton>
                        </Box>

                        <IconButton
                          size="small"
                          color="error"
                          disabled={f.required}
                          onClick={() => deleteField(f.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Paper>
                  )
              )}

              <Button
                size="small"
                onClick={() => addField(g)}
                sx={{
                  mt: 0.75,
                  px: 2,
                  height: 34,
                  fontWeight: 600,
                  textTransform: "none",
                  color: "#fff",
                  borderRadius: 2,
                  background: "linear-gradient(135deg,#f57c00,#ef6c00)",
                  "&:hover": {
                    background: "linear-gradient(135deg,#fb8c00,#f57c00)",
                  },
                }}
              >
                + Add Field
              </Button>
            </Box>
          ))}

          <Box display="flex" justifyContent="center" mt={3} mb={2}>
            <Button
              variant="contained"
              size="large"
              onClick={handleDownloadPdf}
            >
              Generate PDF
            </Button>
          </Box>
        </Box>
      </Paper>
    </>
  );
}

export default FieldsSection;